import { useMemo, useState } from "react";

export type SortDirection = "asc" | "desc";

export type SortValue = string | number | boolean | Date | null | undefined;

export type SortAccessors<T> = Record<string, (row: T) => SortValue>;

function compareValues(a: SortValue, b: SortValue, direction: SortDirection): number {
  const aNull = a == null || a === "";
  const bNull = b == null || b === "";
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;

  let result = 0;
  if (typeof a === "string" && typeof b === "string") {
    result = a.localeCompare(b, undefined, { sensitivity: "base", numeric: true });
  } else if (a instanceof Date && b instanceof Date) {
    result = a.getTime() - b.getTime();
  } else if (typeof a === "boolean" && typeof b === "boolean") {
    result = Number(a) - Number(b);
  } else if (typeof a === "number" && typeof b === "number") {
    result = a - b;
  } else {
    result = String(a).localeCompare(String(b), undefined, { sensitivity: "base", numeric: true });
  }

  return direction === "asc" ? result : -result;
}

export function useTableSort<T>(rows: T[], accessors: SortAccessors<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [direction, setDirection] = useState<SortDirection>("asc");
  function toggle(key: string) {
    if (sortKey === key) {
      setDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setDirection("asc");
    }
  }

  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    const getValue = accessors[sortKey];
    if (!getValue) return rows;
    return [...rows].sort((a, b) => compareValues(getValue(a), getValue(b), direction));
  }, [accessors, rows, sortKey, direction]);

  return { sortKey, direction, toggle, sorted };
}
